import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useLensDatasetHistory, useRestoreLensDataset } from '~/hooks/apis/reporting/useLensDatasetHistory';

import { formatDateShortMonthWithClock } from '~/services/TimeService';

import type { LensDataset } from '~/types/leLens';

export default function DatasetHistoryDialog({
  lensId,
  lensVersion,
  dataset,
  readerCount,
  onClose,
  onRestored,
}: {
  lensId: string;
  lensVersion: number;
  dataset: LensDataset;
  readerCount: number;
  onClose: () => void;
  onRestored: () => void;
}) {
  const history = useLensDatasetHistory(lensId, dataset.id);
  const restore = useRestoreLensDataset(lensId, dataset.id);
  const [selectedVersion, setSelectedVersion] = useState<number | ''>('');
  const selected = history.data?.revisions.find((entry) => entry.version === selectedVersion);
  const current = history.data?.revisions.find((entry) => entry.version === history.data?.currentVersion);
  const changedKeys =
    selected && current
      ? [...new Set([...Object.keys(selected.definition), ...Object.keys(current.definition)])].filter(
          (key) => JSON.stringify(selected.definition[key]) !== JSON.stringify(current.definition[key]),
        )
      : [];
  const [failure, setFailure] = useState('');
  const apply = async () => {
    if (!selected || !dataset.version) {
      return;
    }
    setFailure('');
    try {
      await restore.mutateAsync({ version: selected.version, lensVersion, datasetVersion: dataset.version });
      onRestored();
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not restore this definition.');
    }
  };
  return (
    <Dialog open fullWidth maxWidth="md" onClose={restore.isPending ? undefined : onClose}>
      <DialogTitle>Version history — {dataset.name}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            Current version: {history.data?.currentVersion ?? dataset.version}. Running or refreshing this dataset does
            not create a version.
          </Typography>
          {history.isPending && <Typography>Loading history…</Typography>}
          {history.isError && (
            <Alert severity="error" action={<Button onClick={() => history.refetch()}>Retry</Button>}>
              Could not load version history.
            </Alert>
          )}
          {history.data && (
            <TextField
              select
              label="Saved definition"
              value={selectedVersion}
              onChange={(event) => {
                setSelectedVersion(Number(event.target.value));
                setFailure('');
              }}
              disabled={restore.isPending}
            >
              {history.data.revisions.map((entry) => (
                <MenuItem key={entry.version} value={entry.version}>
                  Version {entry.version} · {formatDateShortMonthWithClock(entry.createdAt)} · {entry.createdBy}
                </MenuItem>
              ))}
            </TextField>
          )}
          {history.data?.revisions.length === 1 && (
            <Alert severity="info">
              History starts with this saved definition. Earlier definitions are not available.
            </Alert>
          )}
          {selected && (
            <>
              <Typography variant="subtitle2">Saved SQL and configuration</Typography>
              <Typography variant="body2">Changes from current: {changedKeys.join(', ') || 'none'}.</Typography>
              <Box
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontSize: 12,
                  maxHeight: 360,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(selected.definition, null, 2)}
              </Box>
              <Alert severity="warning">
                Restore creates version {(history.data?.currentVersion ?? dataset.version ?? 0) + 1} after validation.
                It updates {readerCount} linked {readerCount === 1 ? 'card' : 'cards'} on this Lens. Current names
                and permissions stay in place. Unsaved query edits will be discarded.
              </Alert>
            </>
          )}
          {failure && <Alert severity="error">{failure}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={restore.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            !selected || !changedKeys.length || selected.version === history.data?.currentVersion || restore.isPending
          }
          onClick={apply}
        >
          {restore.isPending ? 'Validating and restoring…' : 'Restore as new version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
